import type {Editor} from "slate";

import {isKhanmigoEditing} from "@khan/guide-math-editor";

import {
    clearEditor,
    getDocumentTitle,
    rerenderTextNodes,
} from "../helpers/editor-helpers.ts";
import {
    insertChunkAtPlaceholder,
    insertChunkAtSelection,
    insertTemplateChunks,
    startKhanmigoEditing,
    stopKhanmigoEditing,
} from "../helpers/insertion-helpers.ts";

/**
 * A Slate plugin that exposes some of our custom helper functions as methods
 * on an editor instance. This is both convenient and important for keeping
 * track of and consolidating all imported functionality.
 *
 * Some of these helpers import other libraries, like `slate-react` or
 * `simple-markdown` (for Markdown parsing). By attaching these helpers to the
 * editor, we can be sure that if we have an editor instance, we can access
 * these - and then we can ensure that we load our editor instance using
 * dynamic imports to avoid downloading all these libraries unless they are
 * actually needed.
 */
export const withHelpers = (editor: Editor) => {
    editor.insertTemplateChunks = (chunks: string[]) => {
        insertTemplateChunks(editor, chunks);
    };

    editor.insertChunkAtPlaceholder = (
        placeholderKey: string,
        chunk: string,
    ) => {
        insertChunkAtPlaceholder(editor, placeholderKey, chunk);
    };

    editor.insertChunkAtSelection = (chunk: string) => {
        insertChunkAtSelection(editor, chunk);
    };

    editor.startKhanmigoEditing = () => {
        startKhanmigoEditing(editor);
    };

    editor.stopKhanmigoEditing = () => {
        stopKhanmigoEditing(editor);
    };

    editor.isKhanmigoEditing = () => {
        return isKhanmigoEditing(editor);
    };

    editor.getDocumentTitle = () => {
        return getDocumentTitle(editor);
    };

    editor.rerenderTextNodes = () => {
        rerenderTextNodes(editor);
    };

    editor.clear = () => {
        clearEditor(editor);
    };

    return editor;
};
